import { useEffect, useMemo, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faSearch, faXmark } from '@fortawesome/free-solid-svg-icons';
import s from './SearchableMultiSelect.module.css';

interface Option {
  value: string;
  label: string;
}

interface SearchableMultiSelectProps {
  options: Option[];
  value: string[];
  onChange: (values: string[]) => void;
  placeholder?: string;
  disabled?: boolean;
  /** Max tags shown inline before collapsing to "N selected" */
  maxTagsShown?: number;
}

export default function SearchableMultiSelect({
  options,
  value,
  onChange,
  placeholder = 'Select…',
  disabled = false,
  maxTagsShown = 3,
}: SearchableMultiSelectProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const wrapRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Focus search when opening
  useEffect(() => {
    if (open) setTimeout(() => searchRef.current?.focus(), 0);
    else setSearch('');
  }, [open]);

  const filtered = useMemo(() => {
    if (!search.trim()) return options;
    const q = search.toLowerCase();
    return options.filter((o) => o.label.toLowerCase().includes(q));
  }, [options, search]);

  const labelById = useMemo(() => {
    const m = new Map<string, string>();
    options.forEach((o) => m.set(o.value, o.label));
    return m;
  }, [options]);

  const toggle = (v: string) => {
    onChange(value.includes(v) ? value.filter((x) => x !== v) : [...value, v]);
  };

  const clearAll = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange([]);
  };

  const removeTag = (e: React.MouseEvent, v: string) => {
    e.stopPropagation();
    onChange(value.filter((x) => x !== v));
  };

  const showCollapsed = value.length > maxTagsShown;

  return (
    <div ref={wrapRef} className={s.wrap}>
      {/* Trigger */}
      <div
        className={`${s.trigger} ${open ? s.triggerOpen : ''} ${disabled ? s.triggerDisabled : ''}`}
        onClick={() => {
          if (!disabled) setOpen((p) => !p);
        }}
        role="combobox"
        aria-expanded={open}
        aria-haspopup="listbox"
      >
        {value.length === 0 ? (
          <span className={s.placeholder}>{placeholder}</span>
        ) : showCollapsed ? (
          <span className={s.count}>{value.length} selected</span>
        ) : (
          <div className={s.tags}>
            {value.map((v) => (
              <span key={v} className={s.tag}>
                {labelById.get(v) ?? v}
                <button
                  type="button"
                  className={s.tagRemove}
                  onClick={(e) => removeTag(e, v)}
                  aria-label={`Remove ${labelById.get(v) ?? v}`}
                >
                  <FontAwesomeIcon icon={faXmark} />
                </button>
              </span>
            ))}
          </div>
        )}
        <FontAwesomeIcon
          icon={faChevronDown}
          className={`${s.chevron} ${open ? s.chevronOpen : ''}`}
        />
      </div>

      {/* Dropdown */}
      {open && (
        <div className={s.dropdown} role="listbox" aria-multiselectable="true">
          {/* Search */}
          <div className={s.searchWrap}>
            <FontAwesomeIcon icon={faSearch} className={s.searchIcon} />
            <input
              ref={searchRef}
              className={s.searchInput}
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search…"
            />
          </div>

          {/* Options */}
          <div className={s.options}>
            {filtered.length === 0 && <div className={s.noResults}>No matches</div>}
            {filtered.map((opt) => {
              const selected = value.includes(opt.value);
              return (
                <div
                  key={opt.value}
                  className={`${s.option} ${selected ? s.optionSelected : ''}`}
                  onClick={() => toggle(opt.value)}
                  role="option"
                  aria-selected={selected}
                >
                  <input
                    type="checkbox"
                    className={s.checkbox}
                    checked={selected}
                    onChange={() => toggle(opt.value)}
                    tabIndex={-1}
                  />
                  {opt.label}
                </div>
              );
            })}
          </div>

          {/* Footer */}
          {value.length > 0 && (
            <div className={s.footer}>
              <button type="button" className={s.clearAll} onClick={clearAll}>
                Clear all ({value.length})
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
